import { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'
import { submitToPortal } from '../lib/portal'
import { useRecaptcha } from '../hooks/useRecaptcha'

const WEBSITE_CHECK = 'Free Website Check'
const TYPES = [WEBSITE_CHECK, 'Full Website Creation', 'Full Web App', 'Online Web Admin Tools', 'Website Hosting', 'Website Upgrade / Small Fixes', 'Stripe / Payment Integration', 'Other']
// /contact?service=hosting (from the Hosting page) pre-picks the hosting option;
// ?service=website-check (the outreach link and hero link) pre-picks the free check.
const SERVICE_PARAMS = { hosting: 'Website Hosting', 'website-check': WEBSITE_CHECK }
const initialType = () => (typeof window !== 'undefined' && SERVICE_PARAMS[new URLSearchParams(window.location.search).get('service')]) || ''
const RATE_LIMIT_MS = 30_000
// Falls back to Google's published reCAPTCHA test key (always passes, shows
// a visible "for testing purposes only" watermark) if VITE_RECAPTCHA_SITE_KEY
// isn't set. Register a real site key at google.com/recaptcha/admin for the
// live domain and set it in .env.local / the deploy's env vars — the
// matching secret key also has to go into CJM Admin's RECAPTCHA_SECRET_KEY
// for server-side verification to actually happen, see server.js there.
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'

function EmailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="M22 7l-10 7L2 7"/>
    </svg>
  )
}
function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
    </svg>
  )
}
function DiscordIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028 14.09 14.09 0 001.226-1.994.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/>
    </svg>
  )
}

const CONTACT_INFO = [
  { Icon: EmailIcon,   label: 'Email',         value: 'cjmstudios.au@gmail.com' },
  { Icon: ClockIcon,   label: 'Response time', value: 'Within 24 hours' },
  { Icon: DiscordIcon, label: 'Discord',        value: 'discord.gg/mtNfz3xgmG', href: 'https://discord.gg/mtNfz3xgmG' },
]

export default function Contact() {
  const [form, setForm]         = useState({ name: '', email: '', type: initialType(), message: '' })
  const [honeypot, setHoneypot] = useState('')
  const [sending, setSending]   = useState(false)
  const [sent, setSent]         = useState(false)
  const [error, setError]       = useState(false)
  const [cooldown, setCooldown] = useState(false)
  const [captchaError, setCaptchaError] = useState(false)
  // Starts loading recaptcha ~600px before this section actually scrolls
  // into view rather than the moment Home mounts — see useRecaptcha.js.
  const { ref: sectionRef, inView: nearContact } = useInView({ triggerOnce: true, rootMargin: '600px 0px' })
  const { containerRef: captchaRef, loaded: captchaLoaded, getResponse: getCaptchaResponse, reset: resetCaptcha } = useRecaptcha(RECAPTCHA_SITE_KEY, nearContact)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const isCheck = form.type === WEBSITE_CHECK
  const pickCheck = () => {
    setForm((f) => ({ ...f, type: WEBSITE_CHECK }))
    document.getElementById('contact-message')?.focus({ preventScroll: true })
  }

  const submit = async (e) => {
    e.preventDefault()
    if (honeypot || cooldown) return
    if (form.name.length > 120 || form.email.length > 254 || form.message.length > 4000) {
      setError(true); return
    }
    const captchaToken = getCaptchaResponse()
    if (!captchaToken) {
      setCaptchaError(true)
      toast.error('Please complete the captcha before sending.')
      return
    }
    setCaptchaError(false)
    setSending(true); setError(false)
    try {
      const res = await submitToPortal('contact', {
        name: form.name.trim(), email: form.email.trim(),
        service: form.type, message: form.message.trim(),
        captchaToken,
      })
      if (res.ok) {
        setSent(true); setCooldown(true)
        toast.success('Message sent — I\'ll be in touch within 24 hours.')
        setTimeout(() => setCooldown(false), RATE_LIMIT_MS)
      } else {
        setError(true)
        toast.error('Something went wrong. Try emailing directly.')
      }
    } catch {
      setError(true)
      toast.error('Network error. Check your connection and try again.')
    }
    finally {
      setSending(false)
      resetCaptcha()
    }
  }

  return (
    <section id="contact" ref={sectionRef} style={{
      padding: '120px 0', background: '#000',
      position: 'relative', overflow: 'hidden',
    }}>
      <div className="bg-orb" style={{
        width: 500, height: 380,
        background: 'rgba(6,182,212,0.05)',
        top: '10%', right: '-8%',
        filter: 'blur(100px)',
      }} />
      <div className="bg-orb" style={{
        width: 360, height: 280,
        background: 'rgba(6,182,212,0.035)',
        bottom: '10%', left: '-6%',
        filter: 'blur(100px)',
      }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 2 }} className="contact-outer">
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr',
          gap: 80, alignItems: 'start',
        }} className="contact-grid">

          {/* Left — info */}
          <motion.div
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="label-xs" style={{ marginBottom: 16 }}>Get in touch</p>
            <h2 className="display-lg heading-glow" style={{ color: '#fff', lineHeight: 1.0, marginBottom: 20 }}>
              Let's build<br />
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>something.</span>
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.5)', fontSize: 14,
              lineHeight: 1.85, maxWidth: 300, marginBottom: 48,
            }}>
              New website, an upgrade to an existing one, Stripe/payment setup, or something else entirely — drop us a message.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {CONTACT_INFO.map(({ Icon, label, value, href }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(6,182,212,0.07)',
                    border: '1px solid rgba(6,182,212,0.14)',
                    color: '#22d3ee',
                  }}>
                    <Icon />
                  </div>
                  <div>
                    <p style={{
                      fontSize: 10, color: 'rgba(255,255,255,0.5)',
                      textTransform: 'uppercase', letterSpacing: '.16em', marginBottom: 3,
                    }}>
                      {label}
                    </p>
                    {href ? (
                      <a href={href} target="_blank" rel="noreferrer" style={{
                        fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500,
                        textDecoration: 'none', transition: 'color 0.2s',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#22d3ee' }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)' }}
                      >
                        {value}
                      </a>
                    ) : (
                      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Free website check — the offer outreach messages point to */}
            <div style={{
              marginTop: 36, padding: '22px 22px 20px', borderRadius: 16,
              background: 'rgba(6,182,212,0.04)',
              border: '1px solid rgba(6,182,212,0.18)',
              maxWidth: 340,
            }}>
              <p className="label-xs" style={{ marginBottom: 10, color: '#22d3ee' }}>Free website check</p>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.75, marginBottom: 16 }}>
                Send your website address and I'll record a short video showing 3 things holding it back
                and how to fix them. No cost, no obligation.
              </p>
              <button type="button" onClick={pickCheck} className="btn-ghost" style={{ fontSize: 13, padding: '10px 18px' }}>
                Get my free check
              </button>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            {sent ? (
              <div style={{
                borderRadius: 20,
                background: 'rgba(6,182,212,0.03)',
                border: '1px solid rgba(6,182,212,0.18)',
                padding: '64px 40px',
                textAlign: 'center',
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', margin: '0 auto 20px',
                  background: 'rgba(6,182,212,0.1)',
                  border: '1px solid rgba(6,182,212,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#22d3ee',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 style={{
                  fontFamily: 'Geist Variable, sans-serif',
                  fontSize: 20, fontWeight: 600, color: '#fff', marginBottom: 10,
                  letterSpacing: '-0.02em',
                }}>
                  Message sent.
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.75 }}>
                  I'll review your project and be in touch shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                style={{
                  borderRadius: 20,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  padding: '36px',
                  display: 'flex', flexDirection: 'column', gap: 18,
                }}
              >
                {/* Honeypot */}
                <input
                  type="text" name="_honey" value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1} autoComplete="off"
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0 }}
                  aria-hidden="true"
                />

                {/* Name + Email row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="form-row">
                  {[
                    { label: 'Name', key: 'name', type: 'text', placeholder: 'Your name' },
                    { label: 'Email', key: 'email', type: 'email', placeholder: 'your@email.com' },
                  ].map(({ label, key, type, placeholder }) => (
                    <div key={key}>
                      <label htmlFor={`contact-${key}`} style={{
                        display: 'block', fontSize: 10, fontWeight: 600,
                        color: 'rgba(255,255,255,0.5)',
                        textTransform: 'uppercase', letterSpacing: '.14em', marginBottom: 8,
                      }}>
                        {label}
                      </label>
                      <input
                        id={`contact-${key}`}
                        type={type} required value={form[key]}
                        onChange={set(key)} placeholder={placeholder}
                        className="input-field"
                        style={{ padding: '13px 16px', fontSize: 13 }}
                      />
                    </div>
                  ))}
                </div>

                {/* Service select */}
                <div>
                  <label htmlFor="contact-type" style={{
                    display: 'block', fontSize: 10, fontWeight: 600,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase', letterSpacing: '.14em', marginBottom: 8,
                  }}>
                    What do you need?
                  </label>
                  <select
                    id="contact-type"
                    required value={form.type} onChange={set('type')}
                    className="input-field"
                    style={{ appearance: 'none', colorScheme: 'dark', fontSize: 13, padding: '13px 16px' }}
                  >
                    <option value="" disabled style={{ background: '#0a0a14' }}>Select an option</option>
                    {TYPES.map((t) => <option key={t} value={t} style={{ background: '#0a0a14' }}>{t}</option>)}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="contact-message" style={{
                    display: 'block', fontSize: 10, fontWeight: 600,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase', letterSpacing: '.14em', marginBottom: 8,
                  }}>
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    required rows={4} value={form.message} onChange={set('message')}
                    placeholder={isCheck ? "Your website address (or business name if you don't have a site yet)" : 'Describe your project…'}
                    className="input-field"
                    style={{ resize: 'none', fontSize: 13, padding: '13px 16px' }}
                  />
                </div>

                {/* Captcha — explicitly rendered by useRecaptcha, not the CSS-class auto-render mode */}
                <div>
                  <div ref={captchaRef} />
                  {!captchaLoaded && (
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Loading captcha…</p>
                  )}
                  {captchaError && (
                    <p style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>
                      Please complete the captcha above before sending.
                    </p>
                  )}
                </div>

                {error && (
                  <p style={{
                    fontSize: 12, color: '#f87171', textAlign: 'center',
                    padding: '10px 16px',
                    background: 'rgba(239,68,68,0.06)',
                    border: '1px solid rgba(239,68,68,0.14)',
                    borderRadius: 8, marginBottom: -4,
                  }}>
                    Something went wrong — please try again or email us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending || cooldown}
                  className="btn-primary"
                  style={{ justifyContent: 'center', opacity: sending || cooldown ? 0.6 : 1, marginTop: 4 }}
                >
                  {sending ? (
                    <>
                      <div style={{
                        width: 13, height: 13,
                        border: '2px solid rgba(255,255,255,0.2)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }} />
                      Sending…
                    </>
                  ) : (
                    <>
                      <span>Send message</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>

      <style>{`
        @media(max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .contact-outer { padding-left: 20px !important; padding-right: 20px !important; }
        }
        @media(max-width: 540px) {
          .form-row { grid-template-columns: 1fr !important; }
          .contact-outer { padding-left: 16px !important; padding-right: 16px !important; }
        }
      `}</style>
    </section>
  )
}
